import { defineStore } from 'pinia'
import { type Ref, ref, watchEffect } from 'vue'
import { DateTime } from 'ts-luxon'
import { useHelpers, useTranslate } from '@/shared/lib'
import type { EventFilterType, EventTypesType } from './types'
import { useLoadEvents } from '@/entities/Events'
import { cloneDeep } from 'lodash'

export const useEvents = defineStore('events', () => {
  const dateFormat: string = "yyyy-LL-dd'T'HH:mm"
  const perPageList = [10, 50, 100]

  const data = ref<any[]>([])
  const total = ref(0)
  const eventTypes = ref<EventTypesType[]>([])
  const callback = ref<Function | null>(null)

  const { trs } = useTranslate()
  const { toTimeFormat } = useHelpers()

  const filter = ref<EventFilterType>({
    date_from: DateTime.now().minus({ day: 3 }).startOf('day').toFormat(dateFormat),
    date_to: DateTime.now().endOf('day').toFormat(dateFormat),
    only_errors: 0,
    event_types: [],
    page: 1,
    per_page: 10,
  })

  /**
   * Настройки шапки формы
   */
  const headers = ref([
    { title: trs('itf.object'), key: 'object.name', sortable: false },
    {
      title: trs('itf.time'),
      key: 'server_created_at',
      sortable: false,
      value: (item: any) => toTimeFormat(item.server_created_at, 'dd.MM.yyyy HH:mm:ss'),
    },
    {
      title: trs('itf.object_time'),
      key: 'device_created_at',
      sortable: false,
      value: (item: any) =>
        item.device_created_at ? toTimeFormat(item.device_created_at, 'dd.MM.yyyy HH:mm:ss') : '-',
    },
    { title: trs('itf.event_type'), key: 'event_type', sortable: false },
    { title: trs('itf.new_value'), key: 'new_value', sortable: false },
  ])

  /**
   * Функция установки и сортировки типов событий
   * @param list
   */
  const setEventType = (list: any[]) => {
    eventTypes.value = [...list].sort((a, b) =>
      a.title.localeCompare(b.title, { sensitivity: 'base' }),
    )
  }

  /**
   * Функция загрузки данных
   */
  const loadData = (objectId: Ref<string>) => {
    const result = useLoadEvents(filter.value, objectId)

    watchEffect(() => {
      if (result.data.value) {
        data.value = cloneDeep(result.data.value.data || [])
        total.value = result.data.value.payload.total
        setEventType(result.data.value.payload.event_types || [])
      }
    })

    return result
  }

  /**
   * Функции установки функции обработки
   * @param value
   */
  const setCallback = (value: Function) => {
    callback.value = value
  }

  /** Функция обновления данных */
  const refetchCallback = () => {
    if (!callback.value) return

    filter.value.page = 1
    callback.value()
  }

  return {
    data,
    total,
    callback,
    dateFormat,
    filter,
    perPageList,
    headers,
    eventTypes,

    loadData,
    setCallback,
    refetchCallback,
  }
})
